(() => {
  const CARD_SELECTOR = '[data-product-card-discounty][data-product-id]';
  const PRODUCT_PRICE_SELECTOR = '[data-discounty-product-price][data-product-id]';
  const BADGE_SELECTOR = '[data-discounty-card-badge]';
  const ENDPOINT = 'https://api-discounty.hengam.io/storefront/graphql';

  const waitForDiscountyConfig = () => new Promise((resolve) => {
    let tries = 0;
    const tick = () => {
      const config = window.$Discounty?.shopData?.shopConfig?.miniVolumeDiscount;
      if (config || tries >= 40) {
        resolve(config || null);
        return;
      }
      tries += 1;
      window.setTimeout(tick, 125);
    };
    tick();
  });

  const getShopDomain = () => (
    window.$Discounty?.shopDomain
    || window.Shopify?.shop
    || window.location.hostname
  );

  const getProductIds = (cards) => {
    return [...new Set(cards
      .map((card) => Number.parseInt(card.dataset.productId, 10))
      .filter(Number.isFinite)
    )];
  };

  const fetchDiscounts = async (productIds) => {
    if (!productIds.length) return [];
    const shopifyDomain = getShopDomain();
    const query = `query GetVolumeDiscounts {
      appEmbed(shopifyDomain: "${shopifyDomain}") {
        variantDiscounts(productIds: [${productIds.join(',')}]) {
          productId
          variantId
          discount {
            discountTiers
            discountType
            operandType
            scopeType
          }
        }
      }
    }`;

    const response = await fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query })
    });

    if (!response.ok) return [];
    const payload = await response.json();
    return payload?.data?.appEmbed?.variantDiscounts || [];
  };

  const getTierValue = (tier) => {
    const value = Number.parseFloat(tier?.discount);
    return Number.isFinite(value) ? value : null;
  };

  const getDiscountedCents = (baseCents, discount, tier) => {
    const value = getTierValue(tier);
    if (value === null) return null;

    const type = String(discount?.discountType || '').toUpperCase();
    if (type.includes('NEW_PRICE')) return Math.round(value * 100);
    if (type.includes('PERCENT')) return Math.round(baseCents * (1 - (value / 100)));
    if (type.includes('AMOUNT') || type.includes('FIXED')) return Math.max(0, baseCents - Math.round(value * 100));
    return null;
  };

  const getBestDiscountCents = (baseCents, discounts) => {
    let best = null;
    discounts.forEach((entry) => {
      let tiers = [];
      try {
        tiers = JSON.parse(entry.discount?.discountTiers || '[]');
      } catch (error) {
        tiers = [];
      }
      tiers.forEach((tier) => {
        const cents = getDiscountedCents(baseCents, entry.discount, tier);
        if (Number.isFinite(cents) && cents > 0 && cents < baseCents) {
          best = best === null ? cents : Math.min(best, cents);
        }
      });
    });
    return best;
  };

  const formatMoney = (cents) => {
    if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents);
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'CAD'
    }).format(cents / 100);
  };

  const renderBadge = (card, cents, config) => {
    const badge = card.querySelector(BADGE_SELECTOR);
    if (!badge) return;

    const styles = JSON.parse(config?.styles || '{}');
    const text = (config?.content?.lowestPriceText || 'As low as {price}')
      .replace('{price}', formatMoney(cents));

    badge.textContent = text;
    badge.style.setProperty('--discounty-card-bg', `#${styles.backgroundColor || 'FD3434'}`);
    badge.style.setProperty('--discounty-card-color', `#${styles.textColor || 'FFFFFF'}`);
    badge.style.setProperty('--discounty-card-border', `#${styles.borderColor || 'FFFFFF'}`);
    badge.style.setProperty('--discounty-card-radius', `${Number.parseInt(styles.borderRadius, 10) || 8}px`);
    badge.hidden = false;
  };

  let initTimer = null;

  const init = async () => {
    const cards = [
      ...document.querySelectorAll(CARD_SELECTOR),
      ...document.querySelectorAll(PRODUCT_PRICE_SELECTOR)
    ];
    if (!cards.length) return;

    const config = await waitForDiscountyConfig();
    if (!config?.isActive) return;

    let discounts = [];
    try {
      discounts = await fetchDiscounts(getProductIds(cards));
    } catch (error) {
      return;
    }

    const byProduct = discounts.reduce((acc, entry) => {
      const id = String(entry.productId);
      acc[id] = acc[id] || [];
      acc[id].push(entry);
      return acc;
    }, {});

    cards.forEach((card) => {
      const baseCents = Number.parseInt(card.dataset.productPriceCents, 10);
      if (!Number.isFinite(baseCents) || baseCents <= 0) return;
      const best = getBestDiscountCents(baseCents, byProduct[card.dataset.productId] || []);
      if (best) renderBadge(card, best, config);
    });
  };

  const scheduleInit = () => {
    window.clearTimeout(initTimer);
    initTimer = window.setTimeout(init, 150);
  };

  const syncEmptyPayplanBlocks = () => {
    document.querySelectorAll('.\\#product-meta-block.\\@payplan').forEach((block) => {
      const mount = block.querySelector('#offerByPriceMsg, .\\#product-payplan');
      if (!mount) return;

      const meaningfulText = mount.textContent
        .replace(/[×✕✖]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
      const hasText = meaningfulText.length > 0;
      const visibleChildren = [...mount.children].some((child) => {
        const childText = child.textContent
          .replace(/[×✕✖]/g, '')
          .replace(/\s+/g, ' ')
          .trim();
        if (!childText) return false;
        const styles = window.getComputedStyle(child);
        const rect = child.getBoundingClientRect();
        return styles.display !== 'none'
          && styles.visibility !== 'hidden'
          && Number.parseFloat(styles.opacity || '1') > 0
          && rect.width > 0
          && rect.height > 0;
      });

      block.dataset.emptyPayplan = hasText || visibleChildren ? 'false' : 'true';
    });
  };

  const schedulePayplanSync = () => {
    syncEmptyPayplanBlocks();
    window.setTimeout(syncEmptyPayplanBlocks, 250);
    window.setTimeout(syncEmptyPayplanBlocks, 1000);
    window.setTimeout(syncEmptyPayplanBlocks, 3000);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      init();
      schedulePayplanSync();
    }, { once: true });
  } else {
    init();
    schedulePayplanSync();
  }

  const observer = new MutationObserver((mutations) => {
    const shouldRun = mutations.some((mutation) => (
      [...mutation.addedNodes].some((node) => (
        node.nodeType === Node.ELEMENT_NODE
        && (
          node.matches?.(CARD_SELECTOR)
          || node.matches?.(PRODUCT_PRICE_SELECTOR)
          || node.querySelector?.(`${CARD_SELECTOR}, ${PRODUCT_PRICE_SELECTOR}`)
        )
      ))
    ));
    if (shouldRun) scheduleInit();
    syncEmptyPayplanBlocks();
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
